import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, AlertTriangle, ChevronRight, User, Clock, ImageOff } from 'lucide-react';
import { formatAge } from '@/utils/ageUtils';
import { formatDateShort } from '@/utils/dateUtils';

// Clean up messy names from data imports
const cleanName = (name) => {
  if (!name) return 'Unknown';
  return name
    .replace(/^:\s*/, '')
    .replace(/\s*\([A-Z]{2}\)\s*$/, '')
    .replace(/\s*\([A-Za-z\s]+\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Calculate time since missing/found
const getTimeSince = (person) => {
  let dateStr;
  if (person.source === 'namus_unclaimed') {
    dateStr = person.date_body_found || person.date_of_death || person.date_found;
  } else if (person.source === 'namus_unidentified') {
    dateStr = person.date_found || person.date_body_found;
  } else {
    dateStr = person.missing_since || person.date_missing || person.missing_date || person.last_seen_date;
  }
  
  if (!dateStr) return null;
  
  try {
    const date = new Date(dateStr);
    const now = new Date();
    
    if (isNaN(date.getTime()) || date > now || date.getFullYear() < 1900) {
      return null;
    }
    
    const years = Math.floor((now - date) / (365.25 * 24 * 60 * 60 * 1000));
    if (years < 1) {
      const months = Math.floor((now - date) / (30 * 24 * 60 * 60 * 1000));
      if (months < 1) {
        const days = Math.floor((now - date) / (24 * 60 * 60 * 1000));
        return `${days}d`;
      }
      return `${months}mo`;
    }
    return `${years}y`;
  } catch {
    return null;
  }
};

// Use centralized date formatting (short format for list items)
const formatDate = (dateStr) => {
  return formatDateShort(dateStr, null);
};

export const CaseListItem = ({ person }) => {
  const [imageError, setImageError] = useState(false);
  const displayName = cleanName(person.name);
  const timeSince = getTimeSince(person);
  const API = process.env.REACT_APP_BACKEND_URL;
  
  const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiB2aWV3Qm94PSIwIDAgNDAwIDQwMCI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNmNWY1ZjUiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSIxNDAiIHI9IjcwIiBmaWxsPSIjZDFkNWRiIi8+PHBhdGggZD0iTTgwIDQwMHYtNDBjMC02Ni4zIDUzLjctMTIwIDEyMC0xMjBoMGM2Ni4zIDAgMTIwIDUzLjcgMTIwIDEyMHY0MCIgZmlsbD0iI2QxZDVkYiIvPjwvc3ZnPg==';
  
  const isNamusUrl = (url) => url?.includes('namus.gov');
  
  const getNamusProxyUrl = (url) => {
    if (!url || !isNamusUrl(url)) return null;
    const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnclaimedPersons|UnidentifiedPersons)\/Cases\/(\d+)\/Images\/(\d+)/);
    if (match) {
      const [, caseType, caseId, imageId] = match;
      return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
    }
    const fallbackMatch = url.match(/Cases\/(\d+)\/Images\/(\d+)/);
    if (fallbackMatch) {
      const [, caseId, imageId] = fallbackMatch;
      let caseType = 'MissingPersons';
      if (person.source === 'namus_unclaimed') caseType = 'UnclaimedPersons';
      else if (person.source === 'namus_unidentified') caseType = 'UnidentifiedPersons';
      return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
    }
    return null;
  };

  const getImage = () => {
    const base64 = person.photo_base64 || person.primary_photo_base64;
    if (base64) {
      return base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
    }
    if (person.photo_url) {
      if (isNamusUrl(person.photo_url)) {
        const proxyUrl = getNamusProxyUrl(person.photo_url);
        if (proxyUrl) return proxyUrl;
      }
      // Convert HTTP to HTTPS to avoid mixed content blocking
      let url = person.photo_url;
      if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://');
      }
      return url;
    }
    return placeholderImage;
  };

  const getLocation = () => {
    // For unidentified remains, this is recovery location
    // For missing persons, this is last seen location
    // The field name is the same but the semantic meaning differs
    if (person.recovery_location) return person.recovery_location;
    if (person.found_location) return person.found_location;
    if (person.last_seen_location) return person.last_seen_location;
    if (person.state) return person.state;
    return null;
  };

  // Get the relevant date label and value
  const getDateInfo = () => {
    const isUnidentified = person.source === 'namus_unclaimed' || 
                           person.source === 'namus_unidentified' ||
                           person.source === 'doenetwork' ||
                           person.case_type === 'unidentified_person' ||
                           person.case_type === 'unidentified';
    
    if (isUnidentified) {
      const date = person.date_found || person.date_body_found;
      return { label: 'Found', date: formatDate(date) };
    }
    const date = person.missing_since || person.date_missing || person.missing_date || person.last_seen_date;
    return { label: 'Since', date: formatDate(date) };
  };

  const dateInfo = getDateInfo();
  const location = getLocation();

  // Source color for the left border
  const getSourceColor = () => {
    switch (person.source) {
      case 'fbi': return 'border-l-red-500';
      case 'namus': return 'border-l-green-500';
      case 'namus_unidentified': return 'border-l-purple-500';
      case 'namus_unclaimed': return 'border-l-rose-500';
      case 'charley': return 'border-l-amber-500';
      case 'doenetwork': return 'border-l-cyan-500';
      case 'ncmec': return 'border-l-blue-500';
      default: return 'border-l-slate-300';
    }
  };

  // Check if we have a real photo (and it hasn't failed to load)
  const hasPhoto = () => {
    if (imageError) return false; // Image failed to load
    const base64 = person.photo_base64 || person.primary_photo_base64;
    if (base64) return true;
    if (person.photo_url && !person.photo_url.includes('fbi.gov')) return true;
    return false;
  };

  const hasRealPhoto = hasPhoto();
  
  // Get state abbreviation for info tile
  const getStateAbbrev = (s) => {
    if (!s) return null;
    if (s.length <= 2) return s.toUpperCase();
    const stateMap = {
      'california': 'CA', 'texas': 'TX', 'florida': 'FL', 'new york': 'NY',
      'pennsylvania': 'PA', 'illinois': 'IL', 'ohio': 'OH', 'georgia': 'GA',
      'north carolina': 'NC', 'michigan': 'MI', 'new jersey': 'NJ', 'virginia': 'VA',
      'washington': 'WA', 'arizona': 'AZ', 'massachusetts': 'MA', 'tennessee': 'TN',
      'indiana': 'IN', 'missouri': 'MO', 'maryland': 'MD', 'wisconsin': 'WI',
      'colorado': 'CO', 'minnesota': 'MN', 'south carolina': 'SC', 'alabama': 'AL',
      'louisiana': 'LA', 'kentucky': 'KY', 'oregon': 'OR', 'oklahoma': 'OK',
      'connecticut': 'CT', 'utah': 'UT', 'iowa': 'IA', 'nevada': 'NV',
      'arkansas': 'AR', 'mississippi': 'MS', 'kansas': 'KS', 'new mexico': 'NM',
      'nebraska': 'NE', 'west virginia': 'WV', 'idaho': 'ID', 'hawaii': 'HI',
      'new hampshire': 'NH', 'maine': 'ME', 'montana': 'MT', 'rhode island': 'RI',
      'delaware': 'DE', 'south dakota': 'SD', 'north dakota': 'ND', 'alaska': 'AK',
      'vermont': 'VT', 'wyoming': 'WY'
    };
    const lower = s.toLowerCase();
    return stateMap[lower] || s.substring(0, 2).toUpperCase();
  };
  
  // Format gender for info tile
  const getGenderDisplay = (g) => {
    if (!g) return null;
    const first = g[0]?.toUpperCase();
    if (first === 'M') return 'Male';
    if (first === 'F') return 'Female';
    return first;
  };

  return (
    <Link 
      to={`/case/${person.id}`}
      className={`flex items-center gap-3 p-2.5 bg-white dark:bg-slate-900 border-l-4 ${getSourceColor()} border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors active:bg-slate-100 dark:active:bg-slate-800`}
      data-testid={`case-list-item-${person.id}`}
    >
      {/* Thumbnail or Info Tile */}
      <div className="relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden">
        {hasRealPhoto ? (
          <>
            <img
              src={getImage()}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover bg-slate-100 dark:bg-slate-800"
              onError={() => setImageError(true)}
            />
            {/* Urgent indicator overlay */}
            {person.is_urgent && (
              <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 flex items-center justify-center rounded-bl">
                <AlertTriangle className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </>
        ) : (
          /* Info Tile when no photo - shows UID/MP, Sex, Age, State */
          <div className={`w-full h-full flex flex-col items-center justify-center p-1 border ${
            person.case_type === 'unidentified_person' || person.source === 'namus_unidentified'
              ? 'bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/40 dark:to-purple-950/30 border-purple-200 dark:border-purple-800'
              : person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed'
              ? 'bg-gradient-to-br from-rose-100 to-rose-50 dark:from-rose-900/40 dark:to-rose-950/30 border-rose-200 dark:border-rose-800'
              : 'bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/40 dark:to-emerald-950/30 border-emerald-200 dark:border-emerald-800'
          }`}>
            {/* Type Label */}
            <div className={`text-[7px] font-bold uppercase tracking-wider leading-none ${
              person.case_type === 'unidentified_person' || person.source === 'namus_unidentified'
                ? 'text-purple-600 dark:text-purple-400'
                : person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed'
                ? 'text-rose-600 dark:text-rose-400'
                : 'text-emerald-600 dark:text-emerald-400'
            }`}>
              {person.case_type === 'unidentified_person' || person.source === 'namus_unidentified' ? 'UID' :
               person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed' ? 'UNC' : 'MP'}
            </div>
            {/* Gender */}
            {getGenderDisplay(person.gender) && (
              <div className={`text-[9px] font-semibold leading-none mt-0.5 ${
                person.case_type === 'unidentified_person' || person.source === 'namus_unidentified'
                  ? 'text-purple-700 dark:text-purple-300'
                  : person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed'
                  ? 'text-rose-700 dark:text-rose-300'
                  : 'text-emerald-700 dark:text-emerald-300'
              }`}>
                {getGenderDisplay(person.gender)}
              </div>
            )}
            {/* Age */}
            {(person.age || person.age_min || person.age_max || person.age_range) && (
              <div className={`text-[9px] leading-none ${
                person.case_type === 'unidentified_person' || person.source === 'namus_unidentified'
                  ? 'text-purple-700 dark:text-purple-300'
                  : person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed'
                  ? 'text-rose-700 dark:text-rose-300'
                  : 'text-emerald-700 dark:text-emerald-300'
              }`}>
                {formatAge(person, { compact: true, showEstimated: false })}
              </div>
            )}
            {/* State */}
            {person.state && (
              <div className={`text-[8px] opacity-80 leading-none ${
                person.case_type === 'unidentified_person' || person.source === 'namus_unidentified'
                  ? 'text-purple-600 dark:text-purple-400'
                  : person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed'
                  ? 'text-rose-600 dark:text-rose-400'
                  : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {getStateAbbrev(person.state)}
              </div>
            )}
            {/* Fallback icon if no data */}
            {!getGenderDisplay(person.gender) && !(person.age || person.age_min || person.age_max || person.age_range) && !person.state && (
              <ImageOff className={`w-4 h-4 opacity-40 mt-0.5 ${
                person.case_type === 'unidentified_person' || person.source === 'namus_unidentified'
                  ? 'text-purple-500'
                  : person.case_type === 'unclaimed_person' || person.source === 'namus_unclaimed'
                  ? 'text-rose-500'
                  : 'text-emerald-500'
              }`} />
            )}
            {/* Urgent indicator */}
            {person.is_urgent && (
              <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 flex items-center justify-center rounded-bl">
                <AlertTriangle className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info - Compact layout */}
      <div className="flex-1 min-w-0">
        {/* Name row */}
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white truncate">
            {displayName}
          </h3>
          {person.is_urgent && (
            <Badge className="bg-red-500 text-white text-[9px] px-1 py-0 h-4 flex-shrink-0">
              URGENT
            </Badge>
          )}
        </div>

        {/* Details row - Age, Sex, Time Missing */}
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {(person.age || person.age_min || person.age_max || person.age_range) && <span>{formatAge(person, { compact: true, showEstimated: false })}</span>}
          {(person.age || person.age_min || person.age_max || person.age_range) && person.gender && <span>•</span>}
          {person.gender && <span className="capitalize">{person.gender}</span>}
          {timeSince && (
            <>
              <span>•</span>
              <span className={`font-medium ${person.is_urgent ? 'text-red-500' : 'text-safety-orange-600'}`}>
                <Clock className="w-3 h-3 inline mr-0.5" />
                {timeSince}
              </span>
            </>
          )}
        </div>

        {/* Location & Date row */}
        <div className="flex items-center gap-3 text-xs text-slate-400 dark:text-slate-500 mt-1">
          {location && (
            <span className="flex items-center gap-1 truncate max-w-[50%]">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{location}</span>
            </span>
          )}
          {dateInfo.date && (
            <span className="flex items-center gap-1 flex-shrink-0">
              <Calendar className="w-3 h-3" />
              {dateInfo.date}
            </span>
          )}
        </div>
      </div>

      {/* Chevron */}
      <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
    </Link>
  );
};

export default CaseListItem;
